import React, { useState, useEffect } from 'react';
import { AddEscalationLevelModal } from './AddEscalationLevelModal';
import { createEscalationMatrixLevel } from '../../api/escalationMatrix';
import { useEscalationMatrixPage, useEscalationMatrixStore } from '../../state/escalationMatrix';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Component for sortable table row
interface SortableTableRowProps {
  level: any;
  index: number;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ level, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: level.id,
    resizeObserverConfig: {}
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-move`}
      {...attributes}
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
        <div className="flex items-center gap-2">
          <div {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
            </svg>
          </div>
          <span>{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
        {level.description || 'No description'}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
        {level.name || 'Unnamed level'}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200">
        {level.notification_methods && Array.isArray(level.notification_methods) 
          ? level.notification_methods.join(', ') 
          : 'No methods'
        }
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        {level.escalation_threshold && level.escalation_threshold > 0 
          ? `After ${Math.floor(level.escalation_threshold / 60)} hours ${level.escalation_threshold % 60} minutes`
          : 'Immediately'
        }
      </td>
    </tr>
  );
};

export const EscalationRules: React.FC = () => {
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const { keycloak } = useKeycloak();
  const params = useParams();
  const projectId = params.projectId || '';
  const { loadEscalationMatrixLevels, updateEscalationLevel } = useEscalationMatrixPage(projectId);
  const { levels } = useEscalationMatrixStore();
  const escalationLevels = levels[projectId] || [];
  
  // Local state for sortable levels
  const [sortableLevels, setSortableLevels] = useState<any[]>([]);

  // Setup sensors for drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Synchronize local state with store data
  useEffect(() => {
    const filtered = escalationLevels
      .filter(level => level && level.id)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
    setSortableLevels(filtered);
  }, [escalationLevels]);

      // Load escalation levels on mount
  useEffect(() => {
    if (projectId) {
      loadEscalationMatrixLevels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleAddLevel = async (levelData: any) => {
    const token = keycloak.token;
    if (!token || !projectId) {
      throw new Error('Authentication required');
    }

    try {
      await createEscalationMatrixLevel(token, projectId, levelData);
      // Update levels list
      await loadEscalationMatrixLevels();
      toast.success('Escalation level created successfully');
    } catch (error) {
      console.error('Failed to create escalation level:', error);
      throw error;
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Save original state for possible rollback
      const originalLevels = [...sortableLevels];
      
      const oldIndex = sortableLevels.findIndex((item) => item.id === active.id);
      const newIndex = sortableLevels.findIndex((item) => item.id === over?.id);
      
      const newOrder = arrayMove(sortableLevels, oldIndex, newIndex);
      
      // Update priorities based on new position
      const updatedLevels = newOrder.map((level, index) => ({
        ...level,
        priority: index + 1
      }));

      console.log('New order:', updatedLevels.map(l => ({ id: l.id, name: l.name, priority: l.priority })));
      
      // Update local state immediately for better UX
      setSortableLevels(updatedLevels);

      // Save changes to server
      try {
        const updatePromises = updatedLevels.map(async (level) => {
          const originalLevel = originalLevels.find(l => l.id === level.id);
          if (originalLevel && originalLevel.priority !== level.priority) {
            console.log(`Updating ${level.name}: priority ${originalLevel.priority} -> ${level.priority}`);
            return updateEscalationLevel(level.id, { priority: level.priority });
          }
        });

        await Promise.all(updatePromises.filter(Boolean));
        
        // Update global store with new data
        const { setLevels } = useEscalationMatrixStore.getState();
        setLevels(projectId, updatedLevels);
        
        toast.success('Priority order updated successfully!');
      } catch (error) {
        console.error('Failed to save priority changes:', error);
        // Revert to original state on error
        setSortableLevels(originalLevels);
        toast.error('Failed to save priority changes. Please try again.');
      }
    }
  };

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Escalation Rules</h3>
          <p className="text-sm text-gray-500 mt-1">
            Drag and drop rows to reorder priority levels
          </p>
        </div>
        <button 
          onClick={() => setShowAddLevelModal(true)}
          className="cursor-pointer bg-white hover:bg-blue-50 border border-gray-100 text-blue-500 font-bold py-2 px-4 rounded-full"
        >
          + Add Level
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                  </svg>
                  Priority
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                Level Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                Notification Methods
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Escalation Timing
              </th>
            </tr>
          </thead>
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <tbody className="bg-white divide-y divide-gray-200">
              {sortableLevels.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                    No escalation levels found. Click "Add Level" to create one.
                  </td>
                </tr>
              ) : (
                <SortableContext 
                  items={sortableLevels.map(level => level.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortableLevels.map((level, index) => (
                    <SortableTableRow 
                      key={level.id} 
                      level={level} 
                      index={index}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </DndContext>
        </table>
      </div>

      <AddEscalationLevelModal
        isOpen={showAddLevelModal}
        onClose={() => setShowAddLevelModal(false)}
        onAddLevel={handleAddLevel}
        projectId={projectId}
      />
    </div>
  );
}; 